"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.presentProfile = void 0;
exports.pronounsAsText = pronounsAsText;
var _solidUi = require("solid-ui");
/* Copied from presenter and modified for new functionallity */

function termValue(term) {
  if (!term) return '';
  if (typeof term === 'string') return term;
  if (typeof term === 'object' && 'value' in term) {
    const value = term.value;
    return typeof value === 'string' ? value : '';
  }
  return '';
}
function isEmailValue(value) {
  const normalized = (value || '').trim();
  if (!normalized) return false;
  if (/^mailto:/i.test(normalized)) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}
function isPhoneValue(value) {
  const normalized = (value || '').trim();
  if (!normalized) return false;
  if (/^tel:/i.test(normalized)) return true;
  return /^[+()\-\s\d]{5,}$/.test(normalized);
}
function resolvePointValueNode(store, entryNode, doc, kind) {
  if (!entryNode) return undefined;
  const expected = kind === 'email' ? isEmailValue : isPhoneValue;
  const entryValue = termValue(entryNode);
  if (expected(entryValue)) return entryNode;
  if (entryNode.termType !== 'NamedNode') return undefined;
  const inDocStatements = store.statementsMatching(entryNode, _solidUi.ns.vcard('value'), null, doc);
  const anyGraphStatements = store.statementsMatching(entryNode, _solidUi.ns.vcard('value'));
  for (const statement of [...inDocStatements, ...anyGraphStatements]) {
    if (expected(termValue(statement.object))) {
      return statement.object;
    }
  }
  return undefined;
}
function resolvePointType(store, entryNode, doc) {
  if (entryNode.termType !== 'NamedNode') return undefined;
  const inDocStatements = store.statementsMatching(entryNode, _solidUi.ns.rdf('type'), null, doc);
  if (inDocStatements.length > 0) return inDocStatements[0].object;
  const anyGraphStatements = store.statementsMatching(entryNode, _solidUi.ns.rdf('type'));
  if (anyGraphStatements.length > 0) return anyGraphStatements[0].object;
  return undefined;
}

/* The following helpers were generated by AI Model: GPT-5.3-Codex. */
/* Prompt: In header selectors select phone and email work type if it exists
           otherwise pick any. */
function isWorkTypeTerm(term) {
  const value = termValue(term).trim().toLowerCase();
  if (!value) return false;
  return value === 'work' || value.endsWith('#work') || value.endsWith('/work');
}
function hasWorkType(store, entryNode, doc) {
  if (entryNode.termType !== 'NamedNode') return false;
  const inDocStatements = store.statementsMatching(entryNode, _solidUi.ns.rdf('type'), null, doc);
  const anyGraphStatements = store.statementsMatching(entryNode, _solidUi.ns.rdf('type'));
  return [...inDocStatements, ...anyGraphStatements].some(statement => isWorkTypeTerm(statement.object));
}
function selectPrimaryPoint(store, subject, predicate, doc, kind, fallbackType) {
  const inDocStatements = store.statementsMatching(subject, predicate, null, doc);
  const anyGraphStatements = store.statementsMatching(subject, predicate);
  const nodes = [...inDocStatements, ...anyGraphStatements].map(statement => statement.object);
  const candidates = [];
  const seen = new Set();
  for (const entryNode of nodes) {
    const key = termValue(entryNode);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    const valueNode = resolvePointValueNode(store, entryNode, doc, kind);
    if (!valueNode) continue;
    const type = resolvePointType(store, entryNode, doc) || fallbackType;
    candidates.push({
      entryNode,
      type,
      valueNode
    });
  }
  const workCandidate = candidates.find(candidate => hasWorkType(store, candidate.entryNode, doc) || isWorkTypeTerm(candidate.type));
  if (workCandidate) return workCandidate;
  if (candidates.length > 0) return candidates[0];
  return undefined;
}
function pronounsAsText(store, subject) {
  let pronouns = store.anyJS(subject, _solidUi.ns.solid('preferredSubjectPronoun')) || '';
  if (pronouns) {
    const them = store.anyJS(subject, _solidUi.ns.solid('preferredObjectPronoun'));
    if (them) {
      pronouns += '/' + them;
    }
  }
  return pronouns || '';
}
function formatLocation(countryName, locality) {
  return countryName && locality ? `${locality}, ${countryName}` : countryName || locality || null;
}
const presentProfile = (subject, store) => {
  const name = _solidUi.utils.label(subject);
  // on contact-pane form it is foaf, but maybe another app saves with vcard.
  const nickname = store.anyValue(subject, _solidUi.ns.vcard('nickname')) || store.anyValue(subject, _solidUi.ns.foaf('nick')) || undefined;
  const dateOfBirth = store.anyValue(subject, _solidUi.ns.vcard('bday')) || undefined;
  const imageSrc = _solidUi.widgets.findImage(subject);
  const jobTitle = store.anyValue(subject, _solidUi.ns.vcard('role')) || undefined;
  const orgName = store.anyValue(subject, _solidUi.ns.vcard('organization-name')) || undefined; // @@ Search whole store
  const doc = subject.doc();

  // Contact info - we will only show one of each type here
  // Just doing for now, but we should change to either store a primary
  // indicator or store it a bit differently in the store.
  const primaryPhone = selectPrimaryPoint(store, subject, _solidUi.ns.vcard('hasTelephone'), doc, 'phone', _solidUi.ns.vcard('Voice'));
  const primaryEmail = selectPrimaryPoint(store, subject, _solidUi.ns.vcard('hasEmail'), doc, 'email', _solidUi.ns.vcard('Internet'));
  const primaryAddressEntryNode = store.any(subject, _solidUi.ns.vcard('hasAddress')) || undefined;
  const primaryAddressType = primaryAddressEntryNode ? store.any(primaryAddressEntryNode, _solidUi.ns.rdf('type')) || undefined : undefined;
  const primaryStreetAddress = primaryAddressEntryNode ? store.anyValue(primaryAddressEntryNode, _solidUi.ns.vcard('street-address')) || undefined : undefined;
  const primaryLocality = primaryAddressEntryNode ? store.anyValue(primaryAddressEntryNode, _solidUi.ns.vcard('locality')) || undefined : undefined;
  const primaryRegion = primaryAddressEntryNode ? store.anyValue(primaryAddressEntryNode, _solidUi.ns.vcard('region')) || undefined : undefined;
  const primaryPostalCode = primaryAddressEntryNode ? store.anyValue(primaryAddressEntryNode, _solidUi.ns.vcard('postal-code')) || undefined : undefined;
  const primaryCountryName = primaryAddressEntryNode ? store.anyValue(primaryAddressEntryNode, _solidUi.ns.vcard('country-name')) || undefined : undefined;
  const primaryAddress = primaryAddressEntryNode ? {
    entryNode: primaryAddressEntryNode,
    type: primaryAddressType,
    streetAddress: primaryStreetAddress,
    locality: primaryLocality,
    region: primaryRegion,
    postalCode: primaryPostalCode,
    countryName: primaryCountryName
  } : undefined;
  const address = primaryAddressEntryNode || null;
  const countryName = address != null ? store.anyValue(address, _solidUi.ns.vcard('country-name')) : null;
  const locality = address != null ? store.anyValue(address, _solidUi.ns.vcard('locality')) : null;
  const pronouns = pronounsAsText(store, subject);
  return {
    name,
    nickname,
    imageSrc,
    dateOfBirth,
    jobTitle,
    orgName,
    primaryPhone,
    primaryEmail,
    primaryAddress,
    location: formatLocation(countryName, locality),
    pronouns,
    entryNode: subject
  };
};
exports.presentProfile = presentProfile;